import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import Reveal from "@/components/site/Reveal";
import EmbedHtml from "@/components/site/EmbedHtml";
import { useSiteContent } from "@/lib/siteContent";
import { useSeo } from "@/lib/useSeo";
import { PageHeader } from "./AboutPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const { content, loading } = useSiteContent();
  useSeo("Booking", "Book Your Party");

  if (loading || !content) return null;

  const embed = (content.site && content.site.bookingFormEmbed) || "";
  if (embed.trim()) {
    return <EmbeddedForm embed={embed} />;
  }
  return <DefaultBookingForm content={content} />;
}

function EmbeddedForm({ embed }) {
  return (
    <div data-testid="booking-page">
      <PageHeader title="Book Your Party" script="Book" subtitle="Tell us about your dream night. We'll handle the magic." />
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <Reveal>
            <EmbedHtml
              html={embed}
              className="bg-white rounded-3xl p-6 lg:p-10 shadow-cozy-lg border border-cozy-gold/15"
              data-testid="booking-embed"
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function DefaultBookingForm({ content }) {
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const themes = content?.themes || [];
  const packages = content?.packages || [];
  const [form, setForm] = useState({
    parent_name: "",
    phone: "",
    email: "",
    event_date: "",
    number_of_teepees: 4,
    theme: params.get("theme") || "",
    package: params.get("package") || "",
    special_requests: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        theme: form.theme || (themes[0]?.id || "custom"),
        package: form.package || (packages[1]?.id || packages[0]?.id),
        number_of_teepees: Number(form.number_of_teepees),
      });
      toast.success("Your booking request was sent! We'll be in touch soon. ✨");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page">
      <PageHeader title="Book Your Party" script="Book" subtitle="Tell us a little about your dream night. We'll handle the magic." />
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          {done ? (
            <Reveal>
              <div className="bg-white rounded-3xl p-12 text-center shadow-cozy-lg border border-cozy-gold/15" data-testid="booking-success">
                <h2 className="font-serif text-4xl">You're <span className="font-script text-cozy-gold">on the list!</span></h2>
                <p className="text-cozy-brown-soft mt-4">We received your request and will reach out within 24 hours to confirm details.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="bg-white rounded-3xl p-8 lg:p-12 shadow-cozy-lg border border-cozy-gold/15 space-y-6" data-testid="booking-form">
                <Field label="Parent Name">
                  <input data-testid="booking-input-name" required value={form.parent_name} onChange={(e) => update("parent_name", e.target.value)} className={inputCls} placeholder="Jane Smith" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Phone"><input data-testid="booking-input-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="(555) 123-4567" /></Field>
                  <Field label="Email"><input data-testid="booking-input-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="you@email.com" /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Event Date"><input data-testid="booking-input-date" required type="date" value={form.event_date} onChange={(e) => update("event_date", e.target.value)} className={inputCls} /></Field>
                  <Field label="Number of Teepees"><input data-testid="booking-input-teepees" required type="number" min="1" max="10" value={form.number_of_teepees} onChange={(e) => update("number_of_teepees", e.target.value)} className={inputCls} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Theme">
                    <select data-testid="booking-input-theme" value={form.theme} onChange={(e) => update("theme", e.target.value)} className={inputCls}>
                      <option value="">Select a theme...</option>
                      {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Package">
                    <select data-testid="booking-input-package" value={form.package} onChange={(e) => update("package", e.target.value)} className={inputCls}>
                      <option value="">Select a package...</option>
                      {packages.map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.price}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Special Requests">
                  <textarea data-testid="booking-input-notes" rows={4} value={form.special_requests} onChange={(e) => update("special_requests", e.target.value)} className={inputCls} placeholder="Allergies, custom colors, favorite characters..." />
                </Field>
                <button data-testid="booking-submit" disabled={submitting} type="submit" className="w-full px-8 py-4 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition-all shadow-cozy disabled:opacity-60">
                  {submitting ? "Sending..." : "Request My Cozy Cove Party"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-3 text-cozy-brown placeholder:text-cozy-brown-soft/50 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
